import React, { useState, useCallback } from 'react';
import { X, Upload, FileText, Trash2, CheckCircle, AlertCircle, Loader2, User, Phone, Mail, MapPin, Briefcase, Building2, GraduationCap, Home, DollarSign, Calendar, FileCheck } from 'lucide-react';
import { sendLoanApplication } from '../../services/emailService';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';

const LoanApplicationModal = ({ isOpen, onClose, product }) => {
    const [step, setStep] = useState(1);
    const [formData, setFormData] = useState({
        // Personal Info
        fullName: '',
        nic: '',
        phone: '',
        email: '',
        address: '',
        // Employment (for applicable loans)
        employerName: '',
        monthlyIncome: '',
        yearsEmployed: '',
        // Business (for business loans)
        businessName: '',
        businessRegNo: '',
        yearsInBusiness: '',
        // Education (for education loans)
        institutionName: '',
        courseName: '',
        // Property (for mortgage loans)
        propertyAddress: '',
        propertyType: '',
        // Loan Details
        amountRequested: '',
        tenure: '',
        purpose: '',
        notes: ''
    });
    const [files, setFiles] = useState([]);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null
    const [dragActive, setDragActive] = useState(false);

    const loanType = product?.title || 'Loan';
    const slug = product?.slug || '';

    // Determine which fields to show based on loan type
    const showEmployment = ['auto-loan', 'personal-loan', 'mortgage-loan', 'leasing'].includes(slug);
    const showBusiness = slug === 'business-loan';
    const showEducation = slug === 'education-loan';
    const showProperty = slug === 'mortgage-loan';

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const handleDrag = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        const droppedFiles = [...e.dataTransfer.files];
        addFiles(droppedFiles);
    }, []);

    const handleFileInput = (e) => {
        const selectedFiles = [...e.target.files];
        addFiles(selectedFiles);
    };

    const addFiles = (newFiles) => {
        const validFiles = newFiles.filter(file => {
            const isValid = file.size <= 5 * 1024 * 1024; // 5MB limit
            const isAllowedType = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'].includes(file.type);
            return isValid && isAllowedType;
        });
        setFiles(prev => [...prev, ...validFiles]);
    };

    const removeFile = (index) => {
        setFiles(prev => prev.filter((_, i) => i !== index));
    };

    const validateStep = (stepNum) => {
        const newErrors = {};

        if (stepNum === 1) {
            if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
            if (!formData.nic.trim()) newErrors.nic = 'NIC number is required';
            if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
            if (!formData.email.trim()) newErrors.email = 'Email is required';
            else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Invalid email format';
            if (!formData.address.trim()) newErrors.address = 'Address is required';
        }

        if (stepNum === 2) {
            if (showEmployment) {
                if (!formData.employerName.trim()) newErrors.employerName = 'Employer name is required';
                if (!formData.monthlyIncome) newErrors.monthlyIncome = 'Monthly income is required';
            }
            if (showBusiness) {
                if (!formData.businessName.trim()) newErrors.businessName = 'Business name is required';
            }
            if (showEducation) {
                if (!formData.institutionName.trim()) newErrors.institutionName = 'Institution name is required';
            }
            if (showProperty) {
                if (!formData.propertyAddress.trim()) newErrors.propertyAddress = 'Property address is required';
            }
        }

        if (stepNum === 3) {
            if (!formData.amountRequested) newErrors.amountRequested = 'Loan amount is required';
            if (!formData.tenure) newErrors.tenure = 'Preferred tenure is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const nextStep = () => {
        if (validateStep(step)) {
            setStep(prev => Math.min(prev + 1, 4));
        }
    };

    const prevStep = () => {
        setStep(prev => Math.max(prev - 1, 1));
    };

    const handleSubmit = async () => {
        if (!validateStep(3)) return;

        setIsSubmitting(true);
        setSubmitStatus(null);

        try {
            const applicationMessage = {
                id: Date.now().toString(),
                type: 'application',
                name: formData.fullName,
                email: formData.email,
                phone: formData.phone,
                nic: formData.nic,
                productTitle: loanType,
                loanAmount: formData.amountRequested,
                tenure: formData.tenure,
                income: formData.monthlyIncome,
                purpose: formData.purpose,
                message: `${loanType} application - Amount: LKR ${formData.amountRequested}, Tenure: ${formData.tenure} months`,
                address: formData.address,
                employerName: formData.employerName,
                businessName: formData.businessName,
                institutionName: formData.institutionName,
                notes: formData.notes,
                read: false,
                created_at: new Date().toISOString()
            };

            // Save to Firestore (primary)
            try {
                const docRef = await addDoc(collection(db, 'messages'), applicationMessage);
                console.log('✅ Application saved to Firestore with ID:', docRef.id);
            } catch (firestoreError) {
                console.error('❌ Firestore write failed:', firestoreError.message);
            }

            // Also save to localStorage (backup)
            const MESSAGES_KEY = 'siteMessages';
            const existingMessages = JSON.parse(localStorage.getItem(MESSAGES_KEY) || '[]');
            existingMessages.unshift(applicationMessage);
            localStorage.setItem(MESSAGES_KEY, JSON.stringify(existingMessages));

            // Dispatch event for real-time notification in admin dashboard
            window.dispatchEvent(new CustomEvent('newMessage', { detail: applicationMessage }));

            // Also try email service (optional)
            try {
                await sendLoanApplication({
                    ...formData,
                    loanType,
                    monthlyIncome: parseFloat(formData.monthlyIncome) || 0,
                    amountRequested: parseFloat(formData.amountRequested) || 0,
                    tenure: parseInt(formData.tenure) || 0
                }, files);
            } catch (emailError) {
                console.warn('Email service error (application saved locally):', emailError);
            }

            setSubmitStatus('success');
            // Reset form after 3 seconds
            setTimeout(() => {
                onClose();
                setStep(1);
                setFormData({
                    fullName: '', nic: '', phone: '', email: '', address: '',
                    employerName: '', monthlyIncome: '', yearsEmployed: '',
                    businessName: '', businessRegNo: '', yearsInBusiness: '',
                    institutionName: '', courseName: '',
                    propertyAddress: '', propertyType: '',
                    amountRequested: '', tenure: '', purpose: '', notes: ''
                });
                setFiles([]);
                setSubmitStatus(null);
            }, 3000);
        } catch (error) {
            console.error('Submission error:', error);
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const requiredDocuments = product?.documents || [
        'NIC copy (front and back)',
        'Income proof',
        'Address verification'
    ];

    return (
        <div className="fixed inset-0 z-[9999] overflow-y-auto flex items-center justify-center">
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="relative w-full max-w-2xl mx-4 my-8 z-[10000]">
                <div
                    className="relative w-full bg-white rounded-2xl shadow-2xl transform transition-all max-h-[90vh] overflow-hidden flex flex-col"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="bg-gradient-to-r from-[#2A9D8F] to-emerald-600 rounded-t-2xl px-6 py-5 flex-shrink-0">
                        <div className="flex items-center justify-between">
                            <div>
                                <h2 className="text-xl font-bold text-white">Apply for {loanType}</h2>
                                <p className="text-white/80 text-sm mt-1">Complete the form below to submit your application</p>
                            </div>
                            <button
                                onClick={onClose}
                                className="text-white/80 hover:text-white transition-colors p-1"
                            >
                                <X size={24} />
                            </button>
                        </div>

                        {/* Progress Steps */}
                        <div className="flex items-center justify-between mt-6">
                            {['Personal Info', 'Details', 'Loan Info', 'Documents'].map((label, idx) => (
                                <div key={idx} className="flex items-center">
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step > idx + 1 ? 'bg-white text-[#2A9D8F]' :
                                        step === idx + 1 ? 'bg-white text-[#2A9D8F]' :
                                            'bg-white/30 text-white'
                                        }`}>
                                        {step > idx + 1 ? <CheckCircle size={18} /> : idx + 1}
                                    </div>
                                    <span className={`ml-2 text-sm hidden sm:block ${step >= idx + 1 ? 'text-white' : 'text-white/60'
                                        }`}>{label}</span>
                                    {idx < 3 && (
                                        <div className={`w-8 sm:w-12 h-0.5 mx-2 ${step > idx + 1 ? 'bg-white' : 'bg-white/30'
                                            }`} />
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Content */}
                    <div className="p-6 overflow-y-auto flex-grow">
                        {submitStatus === 'success' ? (
                            <div className="text-center py-12">
                                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                                    <CheckCircle size={40} className="text-green-500" />
                                </div>
                                <h3 className="text-2xl font-bold text-gray-900 mb-2">Application Submitted!</h3>
                                <p className="text-gray-600">
                                    Thank you for your application. Our team will review it and contact you within 24-48 hours.
                                </p>
                            </div>
                        ) : submitStatus === 'error' ? (
                            <div className="text-center py-12">
                                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                                    <AlertCircle size={40} className="text-red-500" />
                                </div>
                                <h3 className="text-2xl font-bold text-gray-900 mb-2">Submission Failed</h3>
                                <p className="text-gray-600 mb-4">
                                    There was an error submitting your application. Please try again.
                                </p>
                                <button
                                    onClick={() => setSubmitStatus(null)}
                                    className="px-6 py-2 bg-[#2A9D8F] text-white rounded-lg hover:bg-[#238b7f] transition-colors"
                                >
                                    Try Again
                                </button>
                            </div>
                        ) : (
                            <>
                                {/* Step 1: Personal Info */}
                                {step === 1 && (
                                    <div className="space-y-4">
                                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                            <User size={20} className="text-[#2A9D8F]" />
                                            Personal Information
                                        </h3>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Full Name <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    name="fullName"
                                                    value={formData.fullName}
                                                    onChange={handleChange}
                                                    className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2A9D8F] focus:border-transparent outline-none ${errors.fullName ? 'border-red-500' : 'border-gray-300'
                                                        }`}
                                                    placeholder="Enter your full name"
                                                />
                                                {errors.fullName && <p className="text-red-500 text-xs mt-1">{errors.fullName}</p>}
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    NIC Number <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    name="nic"
                                                    value={formData.nic}
                                                    onChange={handleChange}
                                                    className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2A9D8F] focus:border-transparent outline-none ${errors.nic ? 'border-red-500' : 'border-gray-300'
                                                        }`}
                                                    placeholder="Enter NIC number"
                                                />
                                                {errors.nic && <p className="text-red-500 text-xs mt-1">{errors.nic}</p>}
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Phone Number <span className="text-red-500">*</span>
                                                </label>
                                                <div className="relative">
                                                    <Phone size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                                    <input
                                                        type="tel"
                                                        name="phone"
                                                        value={formData.phone}
                                                        onChange={handleChange}
                                                        className={`w-full pl-10 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2A9D8F] focus:border-transparent outline-none ${errors.phone ? 'border-red-500' : 'border-gray-300'
                                                            }`}
                                                        placeholder="+94 XX XXX XXXX"
                                                    />
                                                </div>
                                                {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Email Address <span className="text-red-500">*</span>
                                                </label>
                                                <div className="relative">
                                                    <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                                    <input
                                                        type="email"
                                                        name="email"
                                                        value={formData.email}
                                                        onChange={handleChange}
                                                        className={`w-full pl-10 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2A9D8F] focus:border-transparent outline-none ${errors.email ? 'border-red-500' : 'border-gray-300'
                                                            }`}
                                                        placeholder="your@email.com"
                                                    />
                                                </div>
                                                {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Residential Address <span className="text-red-500">*</span>
                                            </label>
                                            <div className="relative">
                                                <MapPin size={18} className="absolute left-3 top-3 text-gray-400" />
                                                <textarea
                                                    name="address"
                                                    value={formData.address}
                                                    onChange={handleChange}
                                                    rows={2}
                                                    className={`w-full pl-10 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2A9D8F] focus:border-transparent outline-none resize-none ${errors.address ? 'border-red-500' : 'border-gray-300'
                                                        }`}
                                                    placeholder="Enter your full address"
                                                />
                                            </div>
                                            {errors.address && <p className="text-red-500 text-xs mt-1">{errors.address}</p>}
                                        </div>
                                    </div>
                                )}

                                {/* Step 2: Employment/Business/Education Details */}
                                {step === 2 && (
                                    <div className="space-y-4">
                                        {showEmployment && (
                                            <>
                                                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                                    <Briefcase size={20} className="text-[#2A9D8F]" />
                                                    Employment Details
                                                </h3>

                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                            Employer Name <span className="text-red-500">*</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            name="employerName"
                                                            value={formData.employerName}
                                                            onChange={handleChange}
                                                            className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2A9D8F] focus:border-transparent outline-none ${errors.employerName ? 'border-red-500' : 'border-gray-300'
                                                                }`}
                                                            placeholder="Company name"
                                                        />
                                                        {errors.employerName && <p className="text-red-500 text-xs mt-1">{errors.employerName}</p>}
                                                    </div>

                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                            Monthly Income (LKR) <span className="text-red-500">*</span>
                                                        </label>
                                                        <input
                                                            type="number"
                                                            name="monthlyIncome"
                                                            value={formData.monthlyIncome}
                                                            onChange={handleChange}
                                                            className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2A9D8F] focus:border-transparent outline-none ${errors.monthlyIncome ? 'border-red-500' : 'border-gray-300'
                                                                }`}
                                                            placeholder="50000"
                                                        />
                                                        {errors.monthlyIncome && <p className="text-red-500 text-xs mt-1">{errors.monthlyIncome}</p>}
                                                    </div>
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Years Employed
                                                    </label>
                                                    <select
                                                        name="yearsEmployed"
                                                        value={formData.yearsEmployed}
                                                        onChange={handleChange}
                                                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A9D8F] focus:border-transparent outline-none"
                                                    >
                                                        <option value="">Select</option>
                                                        <option value="less-than-1">Less than 1 year</option>
                                                        <option value="1-2">1-2 years</option>
                                                        <option value="2-5">2-5 years</option>
                                                        <option value="5-10">5-10 years</option>
                                                        <option value="10+">More than 10 years</option>
                                                    </select>
                                                </div>
                                            </>
                                        )}

                                        {showBusiness && (
                                            <>
                                                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                                    <Building2 size={20} className="text-[#2A9D8F]" />
                                                    Business Details
                                                </h3>

                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                            Business Name <span className="text-red-500">*</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            name="businessName"
                                                            value={formData.businessName}
                                                            onChange={handleChange}
                                                            className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2A9D8F] focus:border-transparent outline-none ${errors.businessName ? 'border-red-500' : 'border-gray-300'
                                                                }`}
                                                            placeholder="Enter business name"
                                                        />
                                                        {errors.businessName && <p className="text-red-500 text-xs mt-1">{errors.businessName}</p>}
                                                    </div>

                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                            Business Registration No
                                                        </label>
                                                        <input
                                                            type="text"
                                                            name="businessRegNo"
                                                            value={formData.businessRegNo}
                                                            onChange={handleChange}
                                                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A9D8F] focus:border-transparent outline-none"
                                                            placeholder="Registration number"
                                                        />
                                                    </div>
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Years in Business
                                                    </label>
                                                    <select
                                                        name="yearsInBusiness"
                                                        value={formData.yearsInBusiness}
                                                        onChange={handleChange}
                                                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A9D8F] focus:border-transparent outline-none"
                                                    >
                                                        <option value="">Select</option>
                                                        <option value="less-than-1">Less than 1 year</option>
                                                        <option value="1-2">1-2 years</option>
                                                        <option value="2-5">2-5 years</option>
                                                        <option value="5-10">5-10 years</option>
                                                        <option value="10+">More than 10 years</option>
                                                    </select>
                                                </div>
                                            </>
                                        )}

                                        {showEducation && (
                                            <>
                                                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                                    <GraduationCap size={20} className="text-[#2A9D8F]" />
                                                    Education Details
                                                </h3>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Institution Name <span className="text-red-500">*</span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        name="institutionName"
                                                        value={formData.institutionName}
                                                        onChange={handleChange}
                                                        className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2A9D8F] focus:border-transparent outline-none ${errors.institutionName ? 'border-red-500' : 'border-gray-300'
                                                            }`}
                                                        placeholder="University/College name"
                                                    />
                                                    {errors.institutionName && <p className="text-red-500 text-xs mt-1">{errors.institutionName}</p>}
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Course/Program Name
                                                    </label>
                                                    <input
                                                        type="text"
                                                        name="courseName"
                                                        value={formData.courseName}
                                                        onChange={handleChange}
                                                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A9D8F] focus:border-transparent outline-none"
                                                        placeholder="e.g., Bachelor of Engineering"
                                                    />
                                                </div>
                                            </>
                                        )}

                                        {showProperty && (
                                            <>
                                                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2 mt-6">
                                                    <Home size={20} className="text-[#2A9D8F]" />
                                                    Property Details
                                                </h3>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Property Address <span className="text-red-500">*</span>
                                                    </label>
                                                    <textarea
                                                        name="propertyAddress"
                                                        value={formData.propertyAddress}
                                                        onChange={handleChange}
                                                        rows={2}
                                                        className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2A9D8F] focus:border-transparent outline-none resize-none ${errors.propertyAddress ? 'border-red-500' : 'border-gray-300'
                                                            }`}
                                                        placeholder="Full address of the property"
                                                    />
                                                    {errors.propertyAddress && <p className="text-red-500 text-xs mt-1">{errors.propertyAddress}</p>}
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Property Type
                                                    </label>
                                                    <select
                                                        name="propertyType"
                                                        value={formData.propertyType}
                                                        onChange={handleChange}
                                                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A9D8F] focus:border-transparent outline-none"
                                                    >
                                                        <option value="">Select type</option>
                                                        <option value="residential">Residential</option>
                                                        <option value="commercial">Commercial</option>
                                                        <option value="land">Land Only</option>
                                                        <option value="construction">Under Construction</option>
                                                    </select>
                                                </div>
                                            </>
                                        )}

                                        {!showEmployment && !showBusiness && !showEducation && !showProperty && (
                                            <div className="text-center py-8 text-gray-500">
                                                <p>No additional details required for this loan type.</p>
                                                <p className="text-sm mt-2">Click Next to continue.</p>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Step 3: Loan Details */}
                                {step === 3 && (
                                    <div className="space-y-4">
                                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                            <DollarSign size={20} className="text-[#2A9D8F]" />
                                            Loan Details
                                        </h3>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Amount Requested (LKR) <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="number"
                                                    name="amountRequested"
                                                    value={formData.amountRequested}
                                                    onChange={handleChange}
                                                    className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2A9D8F] focus:border-transparent outline-none ${errors.amountRequested ? 'border-red-500' : 'border-gray-300'
                                                        }`}
                                                    placeholder="1000000"
                                                />
                                                {errors.amountRequested && <p className="text-red-500 text-xs mt-1">{errors.amountRequested}</p>}
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Preferred Tenure <span className="text-red-500">*</span>
                                                </label>
                                                <select
                                                    name="tenure"
                                                    value={formData.tenure}
                                                    onChange={handleChange}
                                                    className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2A9D8F] focus:border-transparent outline-none ${errors.tenure ? 'border-red-500' : 'border-gray-300'
                                                        }`}
                                                >
                                                    <option value="">Select tenure</option>
                                                    <option value="12">12 months (1 year)</option>
                                                    <option value="24">24 months (2 years)</option>
                                                    <option value="36">36 months (3 years)</option>
                                                    <option value="48">48 months (4 years)</option>
                                                    <option value="60">60 months (5 years)</option>
                                                    <option value="72">72 months (6 years)</option>
                                                    <option value="84">84 months (7 years)</option>
                                                </select>
                                                {errors.tenure && <p className="text-red-500 text-xs mt-1">{errors.tenure}</p>}
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Purpose of Loan
                                            </label>
                                            <input
                                                type="text"
                                                name="purpose"
                                                value={formData.purpose}
                                                onChange={handleChange}
                                                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A9D8F] focus:border-transparent outline-none"
                                                placeholder="e.g., Vehicle purchase, Business expansion"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Additional Notes
                                            </label>
                                            <textarea
                                                name="notes"
                                                value={formData.notes}
                                                onChange={handleChange}
                                                rows={3}
                                                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A9D8F] focus:border-transparent outline-none resize-none"
                                                placeholder="Any additional information you'd like to share"
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* Step 4: Document Upload */}
                                {step === 4 && (
                                    <div className="space-y-4">
                                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                            <FileCheck size={20} className="text-[#2A9D8F]" />
                                            Upload Documents
                                        </h3>

                                        {/* Required Documents List */}
                                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                                            <h4 className="font-medium text-blue-800 mb-2">Required Documents:</h4>
                                            <ul className="text-sm text-blue-700 space-y-1">
                                                {requiredDocuments.map((doc, idx) => (
                                                    <li key={idx} className="flex items-center gap-2">
                                                        <span className="w-1.5 h-1.5 bg-blue-500 rounded-full" />
                                                        {doc}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>

                                        {/* Drop Zone */}
                                        <div
                                            onDragEnter={handleDrag}
                                            onDragLeave={handleDrag}
                                            onDragOver={handleDrag}
                                            onDrop={handleDrop}
                                            className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${dragActive
                                                ? 'border-[#2A9D8F] bg-[#2A9D8F]/5'
                                                : 'border-gray-300 hover:border-gray-400'
                                                }`}
                                        >
                                            <Upload size={40} className="mx-auto text-gray-400 mb-3" />
                                            <p className="text-gray-600 mb-2">
                                                Drag and drop files here, or{' '}
                                                <label className="text-[#2A9D8F] font-medium cursor-pointer hover:underline">
                                                    browse
                                                    <input
                                                        type="file"
                                                        multiple
                                                        accept=".pdf,.jpg,.jpeg,.png"
                                                        onChange={handleFileInput}
                                                        className="hidden"
                                                    />
                                                </label>
                                            </p>
                                            <p className="text-gray-400 text-sm">
                                                PDF, JPG, PNG (max 5MB each)
                                            </p>
                                        </div>

                                        {/* Uploaded Files */}
                                        {files.length > 0 && (
                                            <div className="space-y-2 mt-4">
                                                <h4 className="font-medium text-gray-700">Uploaded Files:</h4>
                                                {files.map((file, idx) => (
                                                    <div
                                                        key={idx}
                                                        className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-3"
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            <FileText size={20} className="text-[#2A9D8F]" />
                                                            <div>
                                                                <p className="text-sm font-medium text-gray-900">{file.name}</p>
                                                                <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                                                            </div>
                                                        </div>
                                                        <button
                                                            onClick={() => removeFile(idx)}
                                                            className="text-red-500 hover:text-red-700 p-1"
                                                        >
                                                            <Trash2 size={18} />
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {/* Footer */}
                    {!submitStatus && (
                        <div className="border-t px-6 py-4 flex justify-between flex-shrink-0">
                            {step > 1 ? (
                                <button
                                    onClick={prevStep}
                                    className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                                >
                                    Back
                                </button>
                            ) : (
                                <button
                                    onClick={onClose}
                                    className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>
                            )}

                            {step < 4 ? (
                                <button
                                    onClick={nextStep}
                                    className="px-6 py-2.5 bg-[#2A9D8F] text-white rounded-lg hover:bg-[#238b7f] transition-colors"
                                >
                                    Next
                                </button>
                            ) : (
                                <button
                                    onClick={handleSubmit}
                                    disabled={isSubmitting}
                                    className="px-6 py-2.5 bg-[#2A9D8F] text-white rounded-lg hover:bg-[#238b7f] transition-colors flex items-center gap-2 disabled:opacity-70"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <Loader2 size={18} className="animate-spin" />
                                            Submitting...
                                        </>
                                    ) : (
                                        'Submit Application'
                                    )}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default LoanApplicationModal;
